import { Link } from "react-router-dom";
import { ArrowRight, Play, Plus, Minus, User, Calendar, Clock, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { ScheduleConsultationDialog } from "@/components/ScheduleConsultationDialog";
import { DotLottiePlayer } from "@dotlottie/react-player";
import "@dotlottie/react-player/dist/index.css";
import { useRef, useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import heroIllustration from "@/assets/hero-illustration.svg";
import fintechLogo from "@/assets/company-logo-fintech.png";
import foodtechLogo from "@/assets/company-logo-foodtech.png";
import fashiontechLogo from "@/assets/company-logo-fashiontech.png";
import healthtechLogo from "@/assets/company-logo-healthtech.png";
import backgroundNoise from "@/assets/background-noise.svg";
import testimonialMichael from "@/assets/testimonial-michael.jpg";
import testimonialSarah from "@/assets/testimonial-sarah.jpg";
import testimonialDavid from "@/assets/testimonial-david.jpg";
import { blogPosts } from "@/data/blogPosts";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { supabase } from "@/integrations/supabase/client";

const Home = () => {
  const heroReveal = useScrollReveal();
  const servicesReveal = useScrollReveal();
  const workReveal = useScrollReveal();
  const blogReveal = useScrollReveal();
  const ctaReveal = useScrollReveal();
  const lottieRef = useRef<any>(null);
  const [email, setEmail] = useState("");
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    if (servicesReveal.isVisible && lottieRef.current) {
      lottieRef.current.play();
    }
  }, [servicesReveal.isVisible]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('Attempting home newsletter subscription:', email);

      const { data, error } = await supabase.from('leads').insert({
        name: email.split('@')[0], // Use email username as name
        email: email,
        phone: null,
        company: null,
        message: "Newsletter subscription request",
        status: 'new'
      });

      console.log('Home newsletter insert result:', { data, error });

      if (error) {
        console.error('Home newsletter insert error:', error);
        throw error;
      }

      toast({
        title: "Thank you!",
        description: "You're subscribed to our newsletter.",
      });
      setEmail("");
    } catch (error: any) {
      console.error('Home newsletter subscription error:', error);
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-[70vh] md:min-h-[90vh] flex items-center justify-start px-4 pt-24 md:pt-24 pb-12">
        <div 
          ref={heroReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            heroReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            {/* Left: Main Headline */}
            <div className="lg:col-span-7">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[65px] font-semibold mb-4 md:mb-6 leading-[1.1] tracking-tight">
                Focus on What You <span className="text-accent italic">Do Best</span>.
              </h1>
              <p className="text-base md:text-lg text-muted-foreground mb-6 md:mb-8 max-w-xl">
                We streamline your digital marketing so you can focus on your brilliance—the innovation, craftsmanship, and excellence that set you apart in the industry you are competing in and growing in. Guided by values of growth, creativity, collaboration, and integrity, we craft purpose-driven marketing strategies that amplify your vision, boost your performance, and turn your impact into measurable, profitable growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4 items-stretch sm:items-center mb-6 md:mb-8">
                <ScheduleConsultationDialog
                  buttonText="Schedule Call"
                  buttonSize="lg"
                  buttonClassName="rounded-full bg-foreground text-background hover:bg-foreground/90 px-8 w-full sm:w-auto"
                />
                <Button size="lg" variant="outline" className="rounded-full w-full sm:w-auto" asChild>
                  <Link to="/#case-studies">View Case Study</Link>
                </Button>
              </div>
              <div className="flex flex-wrap gap-4 md:gap-6 items-center text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <span className="text-xs">✓</span>
                  </div>
                  <span className="whitespace-nowrap">Strategic SEO</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <span className="text-xs">✓</span>
                  </div>
                  <span className="whitespace-nowrap">Paid Media (PPC)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                    <span className="text-xs">✓</span>
                  </div>
                  <span className="whitespace-nowrap">Content & Copywriting</span>
                </div>
              </div>
            </div>

            {/* Right: Hero Illustration */}
            <div className="lg:col-span-5 flex items-center justify-center mt-8 lg:mt-0">
              <img 
                src={heroIllustration} 
                alt="Digital Marketing Analytics Dashboard" 
                className="w-full max-w-md lg:max-w-xl"
                fetchPriority="high"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-12 md:py-20 px-4">
        <div 
          ref={servicesReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            servicesReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-start mb-8 md:mb-12 pb-6 md:pb-8">
            <div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                Provide the best service with out-of-the-box ideas
              </h2>
            </div>
            <div className="text-muted-foreground">
              <p className="mb-4">
                We blend creative strategy, data-driven insight, and cutting-edge technology to help businesses simplify and scale their digital presence. By combining deep expertise with innovative marketing tools, we create solutions that don't just look good but perform.
              </p>
              <p className="mb-4">
                Our approach is rooted in empowering clients, fostering sustainable progress, and embedding ethical, inclusive practices that benefit both business and community.
              </p>
              <p>
                At the intersection of marketing services and technologies.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Large stat card */}
            <div className="bg-foreground text-background rounded-3xl p-8 md:p-12 shadow-2xl">
              <div className="text-5xl md:text-7xl font-black mb-4">40+</div>
              <p className="text-sm opacity-80 mb-8 md:mb-12">Projects successfully created by Northquest</p>
              
              <div className="flex -space-x-2">
                <div className="w-12 h-12 rounded-full overflow-hidden bg-white border-2 border-foreground">
                  <img src={fintechLogo} alt="Fintech client" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="w-12 h-12 rounded-full overflow-hidden bg-white border-2 border-foreground">
                  <img src={foodtechLogo} alt="Food tech client" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="w-12 h-12 rounded-full overflow-hidden bg-white border-2 border-foreground">
                  <img src={fashiontechLogo} alt="Fashion tech client" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="w-12 h-12 rounded-full overflow-hidden bg-white border-2 border-foreground">
                  <img src={healthtechLogo} alt="Health tech client" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center text-2xl font-bold border-2 border-foreground text-black">+</div>
              </div>
            </div>

            {/* Our Process card */}
            <div className="md:col-span-2 bg-white rounded-3xl p-6 md:p-8 lg:p-10 shadow-lg flex flex-col justify-between relative overflow-hidden">
              <div className="relative z-10 md:pr-32 lg:pr-48">
                <h3 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8">Our Proven Process</h3>
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-accent/30"></div>
                  
                  <div className="space-y-6 md:space-y-8">
                    <div className="flex items-start gap-4 md:gap-6 relative">
                      <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0 relative z-10 ring-4 ring-white"></div>
                      <div className="pt-1">
                        <h4 className="font-semibold text-base md:text-lg mb-1">Strategy & Discovery</h4>
                        <p className="text-sm text-muted-foreground">Deep dive into your brand goals</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4 md:gap-6 relative">
                      <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0 relative z-10 ring-4 ring-white"></div>
                      <div className="pt-1">
                        <h4 className="font-semibold text-base md:text-lg mb-1">Implementation</h4>
                        <p className="text-sm text-muted-foreground">Execute with precision</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-4 md:gap-6 relative">
                      <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center flex-shrink-0 relative z-10 ring-4 ring-white"></div>
                      <div className="pt-1">
                        <h4 className="font-semibold text-base md:text-lg mb-1">Optimize & Scale</h4>
                        <p className="text-sm text-muted-foreground">Continuous growth strategies</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden md:block absolute bottom-0 right-0 w-64 h-64 lg:w-80 lg:h-80 xl:w-96 xl:h-96 z-0">
                <DotLottiePlayer
                  ref={lottieRef}
                  src="https://lottie.host/6167e354-c079-41ad-81fc-4978a9d40b9b/IQp0JqBhYc.lottie"
                  autoplay
                  loop
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section 
        id="case-studies"
        className="py-12 md:py-20 px-4 bg-foreground text-background relative overflow-hidden"
        style={{
          backgroundImage: `url(${backgroundNoise})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div 
          ref={workReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            workReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-4 px-4">
            Real-world examples of how we have helped companies achieve their marketing objectives.
          </h2>
          
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-12 mt-6 md:mt-8">
            <button className="px-4 md:px-6 py-2 rounded-full border border-white/20 hover:bg-white/10 transition-all text-xs md:text-sm">
              Website Marketing
            </button>
            <button className="px-4 md:px-6 py-2 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 transition-all text-xs md:text-sm font-medium">
              Social Media
            </button>
            <button className="px-4 md:px-6 py-2 rounded-full border border-white/20 hover:bg-white/10 transition-all text-xs md:text-sm">
              AI-Powered Marketing
            </button>
            <button className="px-4 md:px-6 py-2 rounded-full border border-white/20 hover:bg-white/10 transition-all text-xs md:text-sm">
              Mobile App
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {/* Case Study 1: E-Commerce Website Transformation */}
            <Link to="/case-studies/ecommerce" className="block">
              <div className="bg-muted/10 rounded-3xl p-6 md:p-8 flex flex-col justify-between hover:bg-muted/20 transition-all cursor-pointer duration-300 min-h-[350px] md:min-h-[400px]">
                <div>
                  <div className="text-accent text-xs md:text-sm font-semibold mb-3">WEBSITE MARKETING</div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">E-Commerce Platform Achieves 320% Traffic Growth</h3>
                  <p className="text-background/80 mb-4">
                    A sustainable fashion retailer was struggling with low organic visibility and poor conversion rates on their e-commerce platform.
                  </p>
                  <p className="text-sm text-accent font-semibold hover:underline">Read full case study →</p>
                </div>
                <div className="grid grid-cols-3 gap-2 md:gap-4 pt-4 md:pt-6 mt-4 md:mt-6 border-t border-white/20">
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-white">320%</div>
                    <div className="text-xs text-white/70">Traffic Increase</div>
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-white">185%</div>
                    <div className="text-xs text-white/70">Conversion Rate</div>
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-white">$2.4M</div>
                    <div className="text-xs text-white/70">Revenue Added</div>
                  </div>
                </div>
              </div>
            </Link>
            
            {/* Case Study 2: Social Media Campaign Success */}
            <Link to="/case-studies/healthtech" className="block">
              <div className="bg-muted/10 rounded-3xl p-6 md:p-8 flex flex-col justify-between hover:bg-muted/20 transition-all cursor-pointer duration-300 min-h-[350px] md:min-h-[400px]">
                <div>
                  <div className="text-accent text-xs md:text-sm font-semibold mb-3">SOCIAL MEDIA MARKETING</div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">HealthTech Startup Builds Engaged Community</h3>
                  <p className="text-background/80 mb-4 text-sm md:text-base">
                    A mental wellness app needed to build brand awareness and drive app downloads in a competitive market.
                  </p>
                  <p className="text-xs md:text-sm text-accent font-semibold hover:underline">Read full case study →</p>
                </div>
                <div className="grid grid-cols-3 gap-2 md:gap-4 pt-4 md:pt-6 mt-4 md:mt-6 border-t border-white/20">
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-white">45K+</div>
                    <div className="text-xs text-white/70">New Followers</div>
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-white">12x</div>
                    <div className="text-xs text-white/70">Engagement</div>
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-white">28%</div>
                    <div className="text-xs text-white/70">Install Rate</div>
                  </div>
                </div>
              </div>
            </Link>
            
            {/* Case Study 3: AI-Powered Marketing Automation */}
            <Link to="/case-studies/fintech" className="block">
              <div className="bg-muted/10 rounded-3xl p-6 md:p-8 flex flex-col justify-between hover:bg-muted/20 transition-all cursor-pointer duration-300 min-h-[350px] md:min-h-[400px]">
                <div>
                  <div className="text-accent text-xs md:text-sm font-semibold mb-3">AI-POWERED MARKETING</div>
                  <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">FinTech Company Scales Customer Acquisition</h3>
                  <p className="text-background/80 mb-4 text-sm md:text-base">
                    A financial services platform needed to personalize campaigns and improve ROI on their marketing spend.
                  </p>
                  <p className="text-xs md:text-sm text-accent font-semibold hover:underline">Read full case study →</p>
                </div>
                <div className="grid grid-cols-3 gap-2 md:gap-4 pt-4 md:pt-6 mt-4 md:mt-6 border-t border-white/20">
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-white">410%</div>
                    <div className="text-xs text-white/70">Growth</div>
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-white">58%</div>
                    <div className="text-xs text-white/70">CAC Reduction</div>
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-white">340%</div>
                    <div className="text-xs text-white/70">ROI Increase</div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="relative">
            {testimonialIndex === 0 && (
              <>
                <blockquote className="text-xl md:text-2xl lg:text-3xl font-medium leading-relaxed mb-6 md:mb-8 text-center px-4">
                  "Northquest transformed our digital presence completely. Their strategic approach to SEO and content marketing helped us achieve a 250% increase in organic traffic within just four months. The team's expertise and dedication to our success has been outstanding."
                </blockquote>
                
                <div className="flex items-center justify-center gap-3 md:gap-4">
                  <img 
                    src={testimonialMichael} 
                    alt="James Robertson, CEO of TechVision Solutions" 
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0"
                    loading="lazy"
                  />
                  <div>
                    <p className="font-semibold text-sm md:text-base">James Robertson</p>
                    <p className="text-xs md:text-sm text-muted-foreground">CEO, TechVision Solutions</p>
                  </div>
                </div>
              </>
            )}

            {testimonialIndex === 1 && (
              <>
                <blockquote className="text-xl md:text-2xl lg:text-3xl font-medium leading-relaxed mb-6 md:mb-8 text-center px-4">
                  "Working with this team has been transformative for our business. Their data-driven campaigns and creative strategies helped us scale from startup to market leader. Professional, responsive, and they truly understand what it takes to grow a brand."
                </blockquote>
                
                <div className="flex items-center justify-center gap-3 md:gap-4">
                  <img 
                    src={testimonialSarah} 
                    alt="Emma Thompson, Marketing Director at InnovateFin" 
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0"
                    loading="lazy"
                  />
                  <div>
                    <p className="font-semibold text-sm md:text-base">Emma Thompson</p>
                    <p className="text-xs md:text-sm text-muted-foreground">Marketing Director, InnovateFin</p>
                  </div>
                </div>
              </>
            )}

            {testimonialIndex === 2 && (
              <>
                <blockquote className="text-xl md:text-2xl lg:text-3xl font-medium leading-relaxed mb-6 md:mb-8 text-center px-4">
                  "The ROI from their PPC campaigns exceeded all expectations. Northquest delivers measurable growth through transparent reporting and strategic optimization. Their ability to understand our business goals and translate them into results is exceptional."
                </blockquote>
                
                <div className="flex items-center justify-center gap-3 md:gap-4">
                  <img 
                    src={testimonialDavid} 
                    alt="Alex Martinez, Founder of Commerce Hub" 
                    className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover flex-shrink-0"
                    loading="lazy"
                  />
                  <div>
                    <p className="font-semibold text-sm md:text-base">Alex Martinez</p>
                    <p className="text-xs md:text-sm text-muted-foreground">Founder, Commerce Hub</p>
                  </div>
                </div>
              </>
            )}

            <div className="flex gap-2 justify-center mt-6 md:mt-8">
              <button 
                onClick={() => setTestimonialIndex((prev) => (prev - 1 + 3) % 3)}
                className="w-10 h-10 rounded-full border border-border hover:bg-muted transition-all flex items-center justify-center"
              >
                ←
              </button>
              <button 
                onClick={() => setTestimonialIndex((prev) => (prev + 1) % 3)}
                className="w-10 h-10 rounded-full bg-foreground text-background hover:bg-foreground/90 transition-all flex items-center justify-center"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-12 md:py-20 px-4">
        <div 
          ref={blogReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            blogReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4">
              Latest <span className="text-accent italic">Insights</span>
            </h2>
            <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto px-4">
              Expert perspectives on digital marketing, AI, and growth strategies that help brands thrive.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {blogPosts.slice(0, 3).map((post) => (
              <Link 
                key={post.id} 
                to={`/blog/${post.slug}`}
                className="group"
              >
                <div className="h-full flex flex-col">
                  <div className="relative h-48 md:h-56 overflow-hidden rounded-3xl mb-4">
                    <img 
                      src={post.image} 
                      alt={post.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-accent text-accent-foreground rounded-full text-xs font-semibold">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col flex-grow">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground mb-3">
                      <div className="flex items-center gap-1">
                        <Calendar size={14} />
                        <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={14} />
                        <span>{post.readTime}</span>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold mb-3 line-clamp-2">
                      {post.title}
                    </h3>
                    
                    <p className="text-muted-foreground text-sm mb-4 flex-grow line-clamp-3">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center text-accent font-semibold text-sm">
                      Read More <ArrowRight size={16} className="ml-1" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8 md:mt-12">
            <Link to="/blog">
              <Button size="lg" variant="outline" className="rounded-full px-8">
                View All Articles
              </Button>
            </Link>
          </div>

          {/* Newsletter Card */}
          <div 
            className="mt-12 md:mt-16 relative overflow-hidden bg-foreground text-background rounded-3xl md:rounded-[3rem] p-8 md:p-12 lg:p-16"
            style={{
              backgroundImage: `url(${backgroundNoise})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Left: Content */}
              <div>
                <h3 className="text-xl md:text-2xl lg:text-3xl font-bold mb-4 flex items-center gap-3">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-accent rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail size={20} className="text-accent-foreground md:w-6 md:h-6" />
                  </div>
                  <span className="sm:whitespace-nowrap">Stay Ahead with Expert Insights</span>
                </h3>
                <p className="text-background/80 text-base md:text-lg">
                  Subscribe to our newsletter for exclusive marketing tips, industry trends, and growth strategies delivered straight to your inbox.
                </p>
              </div>

              {/* Right: Input Field */}
              <div className="mt-4 lg:mt-0">
                <form onSubmit={handleEmailSubmit} className="relative">
                  <Input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="rounded-full px-6 py-6 pr-16 md:pr-20 text-base md:text-lg w-full h-12 md:h-14 bg-background text-foreground border-none"
                    required
                  />
                  <Button type="submit" className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 h-10 w-10 md:h-12 md:w-12 p-0" aria-label="Subscribe to newsletter">
                    <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
                  </Button>
                </form>
                <p className="text-xs text-background/60 mt-4">
                  Join 5,000+ marketers getting our weekly newsletter. Unsubscribe anytime.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-12 md:py-20 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
            {/* Left side - Title and buttons */}
            <div className="lg:sticky lg:top-24">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
                Digital Marketing FAQs
              </h2>
              <p className="text-muted-foreground mb-6 md:mb-8 text-base md:text-lg">
                As a leading digital marketing agency, we are dedicated to providing comprehensive educational resources and answering frequently asked questions to help our clients.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <ScheduleConsultationDialog
                  buttonText="More Questions"
                  buttonSize="lg"
                  buttonVariant="outline"
                  buttonClassName="rounded-full w-full sm:w-auto"
                />
                <ScheduleConsultationDialog
                  buttonText="Contact Us"
                  buttonSize="lg"
                  buttonVariant="ghost"
                  buttonClassName="rounded-full w-full sm:w-auto"
                />
              </div>
            </div>

            {/* Right side - Accordion FAQs */}
            <div>
              <Accordion type="single" collapsible className="space-y-4" defaultValue="item-1">
                <AccordionItem value="item-1" className="border-b border-border pb-4">
                  <AccordionTrigger className="text-lg md:text-xl font-semibold text-left hover:no-underline [&[data-state=open]>svg.plus]:hidden [&[data-state=closed]>svg.minus]:hidden">
                    Why is digital marketing important for my business?
                    <Plus className="plus h-4 w-4 md:h-5 md:w-5 shrink-0 transition-transform duration-200" />
                    <Minus className="minus h-4 w-4 md:h-5 md:w-5 shrink-0 transition-transform duration-200" />
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm md:text-base pt-4">
                    Digital marketing allows businesses to reach and engage with a wider audience, generate leads, drive website traffic, and increase brand visibility. It provides measurable results, allows for targeted marketing efforts, and enables businesses to adapt and optimize their strategies based on data and insights.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2" className="border-b border-border pb-4">
                  <AccordionTrigger className="text-lg md:text-xl font-semibold text-left hover:no-underline [&[data-state=open]>svg.plus]:hidden [&[data-state=closed]>svg.minus]:hidden">
                    How can digital marketing help improve my website's visibility?
                    <Plus className="plus h-4 w-4 md:h-5 md:w-5 shrink-0 transition-transform duration-200" />
                    <Minus className="minus h-4 w-4 md:h-5 md:w-5 shrink-0 transition-transform duration-200" />
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm md:text-base pt-4">
                    Through strategic SEO optimization, content marketing, and paid advertising campaigns, we can significantly improve your website's search engine rankings and online visibility. Our data-driven approach ensures your brand appears in front of the right audience at the right time.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3" className="border-b border-border pb-4">
                  <AccordionTrigger className="text-lg md:text-xl font-semibold text-left hover:no-underline [&[data-state=open]>svg.plus]:hidden [&[data-state=closed]>svg.minus]:hidden">
                    How long does it take to see results from digital marketing efforts?
                    <Plus className="plus h-4 w-4 md:h-5 md:w-5 shrink-0 transition-transform duration-200" />
                    <Minus className="minus h-4 w-4 md:h-5 md:w-5 shrink-0 transition-transform duration-200" />
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm md:text-base pt-4">
                    The timeline varies depending on your goals and the strategies implemented. PPC campaigns can show immediate results, while SEO typically takes 3-6 months to show significant improvements. We provide regular reporting and analytics to track progress and continuously optimize for better results.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-4" className="border-b border-border pb-4">
                  <AccordionTrigger className="text-lg md:text-xl font-semibold text-left hover:no-underline [&[data-state=open]>svg.plus]:hidden [&[data-state=closed]>svg.minus]:hidden">
                    How do you measure the success of digital marketing campaigns?
                    <Plus className="plus h-4 w-4 md:h-5 md:w-5 shrink-0 transition-transform duration-200" />
                    <Minus className="minus h-4 w-4 md:h-5 md:w-5 shrink-0 transition-transform duration-200" />
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm md:text-base pt-4">
                    We track key performance indicators (KPIs) including website traffic, conversion rates, cost per acquisition, return on ad spend (ROAS), engagement metrics, and revenue growth. Our comprehensive analytics dashboards provide transparent insights into campaign performance and ROI.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 px-4">
        <div 
          ref={ctaReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            ctaReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="bg-foreground text-background rounded-3xl md:rounded-[3rem] p-8 md:p-12 lg:p-16 flex flex-col md:flex-row justify-between items-start md:items-center gap-6 md:gap-8">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold max-w-lg">
              Ready to work with us?
            </h2>
            <form onSubmit={handleEmailSubmit} className="relative w-full md:w-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-full px-6 py-6 pr-16 md:pr-20 text-base md:text-lg w-full md:min-w-[350px] h-12 md:h-14 bg-background text-foreground border-none"
                required
              />
              <Button type="submit" className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 h-10 w-10 md:h-12 md:w-12 p-0" aria-label="Submit email">
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Trust & Impact Section */}
      <section className="py-16 md:py-32 px-4 bg-background relative overflow-hidden">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
              Ready to Transform Your <span className="text-accent italic">Digital Presence?</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-8 md:mb-12 leading-relaxed max-w-2xl mx-auto px-4">
              Let's build strategies that drive real growth, create meaningful impact, and position your brand as an industry leader.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center mb-12 md:mb-16 px-4">
              <ScheduleConsultationDialog
                buttonText="Schedule a Consultation"
                buttonClassName="px-6 md:px-8 py-3 md:py-4 bg-foreground text-background rounded-full text-base md:text-lg font-semibold hover:bg-foreground/90 transition-all shadow-lg hover:shadow-xl hover:scale-105"
              >
                <button className="px-6 md:px-8 py-3 md:py-4 bg-foreground text-background rounded-full text-base md:text-lg font-semibold hover:bg-foreground/90 transition-all shadow-lg hover:shadow-xl hover:scale-105">
                  Schedule a Consultation
                </button>
              </ScheduleConsultationDialog>
              <Link to="/#case-studies">
                <button className="px-6 md:px-8 py-3 md:py-4 border-2 border-foreground/20 hover:bg-foreground/5 rounded-full text-base md:text-lg font-semibold transition-all">
                  View Case Studies
                </button>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 md:gap-8 pt-8 md:pt-12 border-t border-foreground/20">
              <div className="flex flex-col items-center">
                <div className="text-4xl md:text-5xl font-bold text-accent mb-2">98%</div>
                <p className="text-muted-foreground text-sm md:text-base">Client Satisfaction</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-4xl md:text-5xl font-bold text-accent mb-2">$50M+</div>
                <p className="text-muted-foreground text-sm md:text-base">Revenue Generated</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-4xl md:text-5xl font-bold text-accent mb-2">15+</div>
                <p className="text-muted-foreground text-sm md:text-base">Years Experience</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-4xl md:text-5xl font-bold text-accent mb-2">200+</div>
                <p className="text-muted-foreground text-sm md:text-base">Brands Served</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
